import type { RootState } from 'state';
import type { Module } from 'types/user/userTypes';
import type { MenuItemShape } from 'comp/layout/comp/sider/comp/menu/types';
import routes from 'router/routes';

import iconsMap from 'comp/layout/comp/sider/comp/menu/config';
import {
  ProductModule,
  ProductSubModuleTourOperator,
  TourOperatorViewBookingsRestrictions,
  TourOperatorViewBookingsPermissions,
  AccommodationPermissions,
} from 'constants/userPermissions/UserPermissions';
import insertAtIndex from 'utils/immutability/insertAtIndex';
import { PATH_TOOLS_SETTINGS } from '../../../../router/constants/routePaths';

const NON_MENU_MODULE_IDS = new Set<number>(
  [
    ...Object.values(TourOperatorViewBookingsRestrictions),
    ...Object.values(TourOperatorViewBookingsPermissions),
    ...Object.values(AccommodationPermissions),
  ].filter((v): v is number => typeof v === 'number')
);

function formatNameAsKey(name: string): string {
  return `${name.replace(/\s+/g, '-').replace('&', 'and').toLowerCase()}`;
}

function formatByOrder(key: string): number {
  switch (key) {
    case 'tour-operator': {
      return 1;
    }
    case 'accommodation': {
      return 2;
    }
    case 'transfers': {
      return 3;
    }
    case 'tools': {
      return 4;
    }
    default: {
      return 5;
    }
  }
}

function transformModuleToMenuItem(item: Module): MenuItemShape {
  const key = formatNameAsKey(item.name);
  const orderForMenu = formatByOrder(key);

  return {
    originalId: item.id,
    key,
    label: `module.label.${key}`,
    description: `module.description.${key}`,
    icon: iconsMap[item.id],
    path: routes?.find((route) => route.componentProps?.moduleId === item.id)?.path,
    orderForMenu,
  };
}

function addAdditionalModules(permittedModules: MenuItemShape[]): MenuItemShape[] {
  const nextPermittedModules = permittedModules;
  const indexOfTools = nextPermittedModules.findIndex((module) => module.originalId === ProductModule.Tools);

  if (indexOfTools !== -1) {
    const toolsModule = nextPermittedModules[indexOfTools];
    const indexOfRG = toolsModule.items?.findIndex(
      (module) => module.originalId === ProductSubModuleTourOperator.ReseguidenLinkGenerator
    );

    if (indexOfRG !== undefined && indexOfRG !== -1) {
      const rgModule = toolsModule.items?.[indexOfRG];

      if (rgModule) {
        const key = `${rgModule.key}-settings`;
        const nextToolsModuleItems = insertAtIndex(toolsModule.items || [], indexOfRG + 1, {
          ...rgModule,
          key,
          label: `module.label.${key}`,
          description: `module.description.${key}`,
          path: PATH_TOOLS_SETTINGS,
        });
        toolsModule.items = nextToolsModuleItems;
        nextPermittedModules[indexOfTools] = toolsModule;
      }
    }
  }

  return nextPermittedModules;
}

function removeModulesForSider(permittedModules: MenuItemShape[]): MenuItemShape[] {
  const modulesToRemove = [
    'documents',
    'packages',
    'email-translator',
    'themes-and-boxes',
    'affiliates',
    'rooms',
    'boards',
    'availability',
    'periods',
    'agegroups',
    'rates',
    'policies',
    'special-offers',
    'rate-plans',
    'packaging-and-extras',
    'cancellation-policies',
    'view-all-hotels-permission',
  ];

  const removedPermittedModules = permittedModules.map((element) => {
    if (element.items) {
      const newElement = { ...element };
      let elementItems: MenuItemShape[] | undefined = [];
      elementItems = element.items.filter((item) => !modulesToRemove.includes(item.key));
      newElement.items = elementItems;
      return newElement;
    }

    return element;
  });
  // Rearanging modules for menu
  removedPermittedModules.sort((a, b) => {
    return a.orderForMenu - b.orderForMenu;
  });

  return removedPermittedModules;
}

function buildMenuTree(allModules: Module[], parentId: number | null): MenuItemShape[] {
  const items = allModules
    .filter((m) => m.parent === parentId && !NON_MENU_MODULE_IDS.has(m.id))
    .map((m) => {
      const children = buildMenuTree(allModules, m.id);
      return {
        ...transformModuleToMenuItem(m),
        items: children.length > 0 ? children : undefined,
      } as MenuItemShape;
    });

  return items.sort((a, b) => a.orderForMenu - b.orderForMenu);
}

function getPermittedModulesForSider(moduleId?: number) {
  return function getPermittedModulesForSiderCallback(state: RootState): MenuItemShape[] {
    if (state.auth.currentUser === null) {
      return [];
    }

    const allModules = state.auth.currentUser.modules;
    let permittedModules: MenuItemShape[];

    if (moduleId) {
      const targetModule = allModules.find((m) => m.id === moduleId);
      if (targetModule) {
        const children = buildMenuTree(allModules, moduleId);
        permittedModules = [
          {
            ...transformModuleToMenuItem(targetModule),
            items: children.length > 0 ? children : undefined,
          },
        ];
      } else {
        permittedModules = [];
      }
    } else {
      permittedModules = buildMenuTree(allModules, null);
    }

    permittedModules = addAdditionalModules(permittedModules);
    permittedModules = removeModulesForSider(permittedModules);

    return permittedModules;
  };
}

export default getPermittedModulesForSider;
